import React from "react";
import "./Brand.scss";

// Hooks
import { useTranslation } from "react-i18next";

// Components
import { faPencil, faStar } from "@fortawesome/pro-regular-svg-icons";
import { IonIcon } from "@ionic/react";
import ColorSection from "../../../../components/Admin/Settings/ColorSection";
import ImageSection from "../../../../components/Admin/Settings/ImageSection";
import SubTitle from "../../../../components/Admin/Settings/SubTitle";

// Types
import { CurrentSegmentComponentProps } from "../SettingsTypes";

export default function BrandSegment(props: CurrentSegmentComponentProps) {
  const { formData, updateFormData, errors } = props;
  const { t } = useTranslation();

  return (
    <>
      {/* Logos */}
      <SubTitle icon={faStar} text="Logos" />
      <ImageSection
        title={`${t("adminAreaHeaderLogo")} *`}
        value={formData?.brand?.img_path_admin_header_logo}
        changeHandler={updateFormData}
        name="brand.img_path_admin_header_logo"
        errors={errors}
        minDimensions={{ width: 200, height: 80 }}
        exampleImage={require("../../../../assets/images/admin-area-header-logo-example.png")}
      />
      <ImageSection
        title={`${t("loginScreenLogo")} *`}
        value={formData?.brand?.img_path_login_screen_logo}
        changeHandler={updateFormData}
        name="brand.img_path_login_screen_logo"
        errors={errors}
        minDimensions={{ width: 300, height: 200 }}
        exampleImage={require("../../../../assets/images/login-screen-logo-example.png")}
      />
      <ImageSection
        title={`${t("appHeaderLogo")} *`}
        value={formData?.brand?.img_path_app_header_logo}
        changeHandler={updateFormData}
        name="brand.img_path_app_header_logo"
        errors={errors}
        minDimensions={{ width: 200, height: 200 }}
        exampleImage={require("../../../../assets/images/app-header-logo-example.png")}
      />

      {/* Colors */}
      <SubTitle icon={faPencil} text="Colours" />
      <ColorSection
        elementColors={[{ name: "brand.color_app_background", label: t("appBackground"), value: formData?.brand?.color_app_background }]}
        textColors={[{ name: "brand.color_app_text", label: t("mainText"), value: formData?.brand?.color_app_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[
          { name: "brand.color_accent", label: t("accent"), value: formData?.brand?.color_accent },
          { name: "brand.color_accent_hover", label: `${t("accent")} - ${t("hover")}`, value: formData?.brand?.color_accent_hover },
        ]}
        textColors={[{ name: "brand.color_accent_text", label: t("accentButtonText"), value: formData?.brand?.color_accent_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[
          { name: "brand.color_error", label: t("errorSlashWarning"), value: formData?.brand?.color_error },
          { name: "brand.color_error_hover", label: `${t("errorSlashWarning")} - ${t("hover")}`, value: formData?.brand?.color_error_hover },
        ]}
        textColors={[{ name: "brand.color_error_text", label: t("errorSlashWarningButtonText"), value: formData?.brand?.color_error_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[
          { name: "brand.color_success", label: t("success"), value: formData?.brand?.color_success },
          { name: "brand.color_success_hover", label: `${t("success")} - ${t("hover")}`, value: formData?.brand?.color_success_hover },
        ]}
        textColors={[{ name: "brand.color_success_text", label: t("successButtonText"), value: formData?.brand?.color_success_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[{ name: "brand.color_disabled", label: t("disabledSlashPrompt"), value: formData?.brand?.color_disabled }]}
        textColors={[{ name: "brand.color_disabled_text", label: t("disabledSlashPromptButtonText"), value: formData?.brand?.color_disabled_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[
          { name: "brand.color_breadcrumb_background", label: t("breadcumbBar"), value: formData?.brand?.color_breadcrumb_background },
          { name: "brand.color_header", label: t("header"), value: formData?.brand?.color_header },
          { name: "brand.color_header_accent", label: t("headerAccent"), value: formData?.brand?.color_header_accent },
        ]}
        textColors={[{ name: "brand.color_header_text", label: t("headerText"), value: formData?.brand?.color_header_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[{ name: "brand.color_navigation", label: t("navigationAccent"), value: formData?.brand?.color_navigation }]}
        textColors={[{ name: "brand.color_navigation_text", label: t("navigationAccentText"), value: formData?.brand?.color_navigation_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[
          { name: "brand.color_chat_message_in", label: t("chatMessagesIn"), value: formData?.brand?.color_chat_message_in },
          { name: "brand.color_chat_message_out", label: t("chatMessagesOut"), value: formData?.brand?.color_chat_message_out },
        ]}
        textColors={[{ name: "brand.color_chat_text", label: t("chatText"), value: formData?.brand?.color_chat_text }]}
        changeHandler={updateFormData}
      />
      <ColorSection
        elementColors={[{ name: "brand.color_profile_notification", label: t("profileNotifications"), value: formData?.brand?.color_profile_notification }]}
        textColors={[{ name: "brand.color_profile_notification_text", label: t("profileNotificationsText"), value: formData?.brand?.color_profile_notification_text }]}
        changeHandler={updateFormData}
      />

      {/* Gradients */}
      <SubTitle iconElement={<IonIcon src="../../assets/icons/gradient.svg" slot="icon" />} text="Gradients" />

      <h3>{t("mobileModalsAndSidePanels")} *</h3>

      <ColorSection
        elementColors={[
          { name: "brand.color_modal_gradient1", label: t("color") + " 1", value: formData?.brand?.color_modal_gradient1 },
          { name: "brand.color_modal_gradient2", label: t("color") + " 2", value: formData?.brand?.color_modal_gradient2 },
        ]}
        textColors={[{ name: "brand.color_modal_text", label: t("modalText"), value: formData?.brand?.color_modal_text }]}
        changeHandler={updateFormData}
        gradientPreview
        noBorder
      />

      <h3>{t("loginPagesBackground")} *</h3>

      <ColorSection
        elementColors={[{ name: "brand.color_login_background", label: t("color") + " 1", value: formData?.brand?.color_login_background }]}
        textColors={[{ name: "brand.color_login_background_text", label: t("text"), value: formData?.brand?.color_login_background_text }]}
        changeHandler={updateFormData}
        noBorder
      />

      <h3>{t("loginLogoPanelBackground")} *</h3>

      <ColorSection
        elementColors={[{ name: "brand.color_login_logo_background", label: t("color") + " 1", value: formData?.brand?.color_login_logo_background }]}
        textColors={[{ name: "brand.color_login_logo_background_text", label: t("text"), value: formData?.brand?.color_login_logo_background_text }]}
        changeHandler={updateFormData}
        noBorder
      />

      <ImageSection
        title="Login Background Image - Desktop"
        value={formData?.brand?.img_path_login_background}
        changeHandler={updateFormData}
        text={t("pleaseUploadYourImage") || ""}
        label={t("uploadYourImage") || ""}
        name="brand.img_path_login_background"
        errors={errors}
        minDimensions={{ width: 1080, height: 1920 }}
      />
    </>
  );
}
